import { Button, Card, LoadingSpinner, SubmitBar } from "@rothoblaas/design-system";
import { FC, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuthContext } from "../../context/authContext";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import useI18n from "../../hooks/useTranslations";
import { InviteProjectData } from "../../http/types/projectSharingInvites";
import { useInvitesApi } from "../../http/useInvites";
import { useProjectSharingInvitesApi } from "../../http/useProjectSharingInvites";
import styles from "./ManageProjectInvite.module.css";

const ManageProjectInvite: FC = () => {
  const i18n = useI18n();
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const dispatch = useContext(NotificationDispatch);
  const auth = useAuthContext();
  const invitesApi = useInvitesApi();
  const projectSharingInvitesApi = useProjectSharingInvitesApi();

  const [invite, setInvite] = useState<InviteProjectData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [clicked, setClicked] = useState(false);

  const searchParams = new URLSearchParams(window.location.search);
  const isAcceptCallback = searchParams.get("accepted") === "true";

  const loadInvite = () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    invitesApi
      .getProjectData(token)
      .then((data) => {
        setInvite(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err?.message ?? "Failed to load invite");
        setLoading(false);
      });
  };

  useEffect(() => {
    loadInvite();
  }, [token]);

  useEffect(() => {
    if (isAcceptCallback && auth.identity && token && invite && !clicked) {
      handleAccept();
    }
  }, [auth.identity, invite]);

  const handleAccept = () => {
    if (!token) return;

    if (!auth.identity) {
      const acceptUrl = new URL(window.location.href);
      acceptUrl.searchParams.set("accepted", "true");
      // The accounts login/register pages read `email` and `email_locked` from their own query,
      // not from inside redirect_uri, so they go on the login URL, not the accept URL.
      const loginParams = new URLSearchParams({ redirect_uri: acceptUrl.toString() });
      if (invite?.invitedEmail) {
        loginParams.set("email", invite.invitedEmail);
        loginParams.set("email_locked", "1");
      }
      const iamUrl = import.meta.env.VITE_IAM_URL || "";
      window.location.href = `${iamUrl}?${loginParams.toString()}`;
      return;
    }

    setIsSubmitting(true);

    projectSharingInvitesApi
      .acceptSharingInvite(token)
      .then(({ projectId }) => {
        setClicked(true);
        dispatch(
          showSuccessNotification(i18n.translation, i18n.translation.projects.invitationStatus.statusChanges.accept),
        );
        navigate(`/my-projects/${projectId}`);
      })
      .catch((err) => {
        dispatch(showErrorNotification(i18n, err));
      })
      .finally(() => setIsSubmitting(false));
  };

  const handleRefuse = () => {
    if (!token) return;
    setIsSubmitting(true);

    invitesApi
      .refuseSharingInvite(token)
      .then(() => {
        setClicked(true);
        dispatch(
          showSuccessNotification(i18n.translation, i18n.translation.projects.invitationStatus.statusChanges.rejected),
        );
        navigate("/");
      })
      .catch((err) => {
        dispatch(showErrorNotification(i18n, err));
      })
      .finally(() => setIsSubmitting(false));
  };

  const authorName =
    [invite?.author?.firstName, invite?.author?.lastName].filter(Boolean).join(" ") ||
    i18n.translation.projects.invitePage.fallbackAuthorName;

  if (loading) {
    return (
      <div className={styles.centerContent}>
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !invite) {
    return (
      <div className={styles.centerContent}>
        <div className={styles.inviteHero}>
          <p className={styles.lead}>{error ?? "Invite not found"}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.centerContent}>
      <Card shadow className={styles.card}>
        <div className={styles.inviteHero}>
          <span className={styles.badge}>{i18n.translation.projects.invitePage.badge}</span>
          <h2 className={styles.projectName}>{invite.name}</h2>
          <p className={styles.lead}>
            <strong>{authorName}</strong> {i18n.translation.projects.invitePage.collaborationLead}
          </p>
        </div>

        <div className={styles.detailsGrid}>
          <div className={styles.detailCard}>
            <span className={styles.detailLabel}>{i18n.translation.projects.invitePage.invitedBy}</span>
            <span className={styles.detailValue}>{authorName}</span>
          </div>
          <div className={styles.detailCard}>
            <span className={styles.detailLabel}>{i18n.translation.projects.invitePage.invitationSentTo}</span>
            <span className={styles.detailValue}>{invite.invitedEmail}</span>
          </div>
        </div>

        <div className={styles.summary}>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>{i18n.translation.projects.invitePage.projectName}</span>
            <span className={styles.summaryValue}>{invite.name}</span>
          </div>
        </div>

        <div className={styles.notice}>
          {!invite.actionRequired
            ? i18n.translation.projects.invitePage.inviteNoLongerAvailable
            : auth.identity
              ? i18n.translation.projects.invitePage.acceptSignedIn
              : i18n.translation.projects.invitePage.acceptSignedOut}
        </div>

        {invite.actionRequired && (
          <div className={styles.actions}>
            <SubmitBar>
              <Button disabled={isSubmitting || clicked} onClick={handleAccept}>
                {i18n.translation.projects.invitePage.acceptAction}
              </Button>
              <Button variant="secondary" disabled={isSubmitting || clicked} onClick={handleRefuse}>
                {i18n.translation.projects.invitePage.rejectAction}
              </Button>
            </SubmitBar>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ManageProjectInvite;
